/**
 * GroupInventorySection — Sprint 2 (FE-9.3).
 *
 * Renders the items bound to a Crew. Owns:
 *  - Initial load + paginated infinite scroll (50 per page)
 *  - Empty state when the Crew has no items yet
 *  - Loading skeleton during the first fetch
 *  - Error banner with pull-to-retry
 *  - Per-row remove action (OWNER/ADMIN only)
 *
 * Lives as a flat section on `GroupDetailScreen`, sandwiched between
 * Members and Settings. The screen renders sections inline (rather
 * than as React Navigation tabs) — that's the established Sprint 1
 * pattern; this issue keeps the precedent.
 *
 * Pagination: we use a simple manual cursor (`nextOffset` from the
 * slice) and a "Load more" footer button rather than `onEndReached`
 * because Group Detail is a vertical scroll surface that nests
 * multiple sections — `onEndReached` would fire too early as the
 * outer scroll moves through Settings. A footer button is
 * predictable.
 *
 * Single-row remove: tapping the trash icon opens a confirmation
 * dialog ("Remove X from {crew name}?"). Confirm fires the thunk;
 * the row is optimistically removed (slice handles the rollback if
 * the network call fails).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { ItemThumbnail } from '../../../components/ItemThumbnail';

import Toast from 'react-native-toast-message';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import {
  loadGroupInventory,
  removeItemFromGroup,
  selectGroupDetail,
  selectGroupInventory,
} from '../groupsSlice';
import { selectUser } from '../../auth/authSlice';
import { returnItem } from '../../inventory/custodySlice';
import type { GroupItemSummary } from '../../../api/types';
import { api } from '../../../api/remembrApi';
import { AddItemsToGroupSheet } from './AddItemsToGroupSheet';
import { RequestHandoffSheet } from '../../inventory/components/RequestHandoffSheet';
// FE-220 hotfix — surface "Requested" state on the Borrow CTA when the
// viewer already has a PENDING borrow request on this row's item. Without
// this, the user can fire duplicate handoff-requests; each one notifies
// the owner separately and the owner's inbox fills with dupes. Backend
// idempotency may dedupe at the DB level, but the FE still needs to
// reflect the live state so the requester stops tapping.
import {
  buildPendingRequestsByItem,
  loadReceivedRequests,
  loadSentRequests,
  selectReceivedRequests,
  selectSentRequests,
} from '../../borrowRequests/borrowRequestsSlice';
// FE-248 v2 — inline strip under each row + sticky banner on overflow.
import { InventoryRowPendingStrip } from '../../borrowRequests/components/InventoryRowPendingStrip';
import { InventoryPendingSummaryBanner } from '../../borrowRequests/components/InventoryPendingSummaryBanner';

const PAGE_SIZE = 50;

/**
 * Gap #111 (2026-05-27 audit) — Relative-short formatter for custody
 * meta lines ("Lent out · Dylan · today"). The previous "${days}d"
 * shorthand produced "0d" for <24h handoffs, which read as "zero
 * days, didn't happen" — confusing to anyone scanning the row.
 *
 * Buckets (matching iOS Mail / Slack patterns):
 *   <1h     → "just now"
 *   1-23h   → "today"
 *   24-47h  → "yesterday"
 *   2-6d    → "{N}d"
 *   7+d     → "{N}d"            (unchanged — long-tail is fine numeric)
 */
function formatRelativeShort(ts: number): string {
  const ms = Date.now() - ts;
  if (ms < 60 * 60 * 1000) return 'just now';
  if (ms < 24 * 60 * 60 * 1000) return 'today';
  if (ms < 48 * 60 * 60 * 1000) return 'yesterday';
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  return `${days}d`;
}

export interface GroupInventorySectionProps {
  groupId: number;
  groupName: string;
  /** Destructive operations (unbind someone else's item) stay
   *  gated to OWNER/ADMIN. Member-row remove uses the same gate
   *  from the parent. */
  isAdmin: boolean;
  /** PR31 — any ACTIVE member can add items to the group's
   *  shared inventory. Defaults to `isAdmin` for back-compat when
   *  the caller doesn't supply this prop. Founder decision: keep
   *  the permission model simple for v1; permissions UX (per-member
   *  read/write toggles) is deferred. */
  canAddItems?: boolean;
}

/* ── public component ─────────────────────────────────────────────── */

export const GroupInventorySection: React.FC<GroupInventorySectionProps> = ({
  groupId,
  groupName,
  isAdmin,
  canAddItems,
}) => {
  // `canAddItems` falls back to `isAdmin` so existing call sites
  // that haven't migrated keep their old behavior. The new
  // GroupDetailScreen wire-up passes `canAddItems={!!myRole}`.
  const canAdd = canAddItems ?? isAdmin;
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const cache = useAppSelector(selectGroupInventory(groupId));
  const detail = useAppSelector(selectGroupDetail(groupId));
  const currentUser = useAppSelector(selectUser);
  const viewerId = currentUser?.id ?? null;
  const [addSheetOpen, setAddSheetOpen] = useState(false);
  // Search-UX §3.6 — local client-side filter over the items already
  // loaded from /groups/{id}/inventory.  Matches case-insensitively
  // against item_name, item_category, and tags.  Pagination collapses
  // while a query is active (we filter the local set, not the server
  // page).  Empty query is a no-op — list reflects full pagination.
  const [searchQuery, setSearchQuery] = useState('');
  // FE-203 — local borrow-request sheet visibility + which item it's
  // for. We can't keep the open state on the row because the same
  // modal needs to mount above the whole section (z-index, only one
  // can be open at a time, and we share state across rows).
  const [borrowFor, setBorrowFor] = useState<GroupItemSummary | null>(null);
  const [handingBackId, setHandingBackId] = useState<number | null>(null);

  // FE-220 hotfix — sent-borrow-requests cache. Used to compute the
  // per-row "Requested" pill that replaces [Borrow] when the viewer
  // already has a PENDING outgoing request on that item_id. We refetch
  // when this section mounts so the cache reflects the most recent
  // server state (e.g. user got the row approved on another device).
  const sentRequests = useAppSelector(selectSentRequests);
  useEffect(() => {
    dispatch(loadSentRequests() as any);
    // Cheap to refetch — runs once per group mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);
  // FE-248 v2 — Stale-while-revalidate: render whatever's in the
  // received-requests cache immediately (already populated by
  // `useNotificationActions` on app mount). Fire-and-forget refetch
  // here keeps it warm without gating render on the network call.
  const allReceived = useAppSelector(selectReceivedRequests);
  useEffect(() => {
    dispatch(loadReceivedRequests({}) as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);
  // Memoized per-item Map<item_id, requests[]> for THIS group. O(N)
  // once per cache change; rows do O(1) lookups.
  const pendingByItem = useMemo(
    () => buildPendingRequestsByItem(allReceived ?? [], groupId),
    [allReceived, groupId],
  );
  // Summary metrics for the sticky banner (overflow case).
  const pendingSummary = useMemo(() => {
    let totalRequests = 0;
    let expiringSoonCount = 0;
    for (const [, list] of pendingByItem) {
      totalRequests += list.length;
      for (const r of list) {
        const d = (r as any).days_until_expiry;
        if (typeof d === 'number' && d <= 1 && d >= 0) expiringSoonCount++;
      }
    }
    return {
      itemsWithPending: pendingByItem.size,
      totalRequests,
      expiringSoonCount,
    };
  }, [pendingByItem]);
  const pendingRequestItemIds = useMemo(() => {
    const s = new Set<number>();
    for (const r of sentRequests ?? []) {
      const status = String((r as any).status ?? '').toUpperCase();
      // Treat PENDING / WAITLISTED as "awaiting owner action". Other
      // states (APPROVED / DENIED / EXPIRED / CANCELLED / FULFILLED)
      // mean the row is resolved — the viewer can re-request if they
      // want to (e.g. after a DENIED, they may try again later).
      if (status === 'PENDING' || status === 'WAITLISTED') {
        const itemId = (r as any).item_id;
        if (typeof itemId === 'number') s.add(itemId);
      }
    }
    return s;
  }, [sentRequests]);

  // FE-203 — map ownerUserId → display name for the row's "owned by X"
  // line. Falls back to "another member" when the member cache hasn't
  // hydrated (group_detail not loaded yet).
  const memberNameById = useMemo<Record<number, string>>(() => {
    const out: Record<number, string> = {};
    for (const m of detail?.members ?? []) {
      if (m.user_id != null && m.name) out[m.user_id] = m.name;
    }
    return out;
  }, [detail]);

  const rawItems = cache?.items ?? [];
  // Search-UX §3.6 — case-insensitive substring match across the
  // fields a user might recall: name, category, tags string.  Skip
  // the filter when the query is empty so the existing load-more
  // pagination behaves normally.
  const items = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rawItems;
    return rawItems.filter((it: any) => {
      if ((it.item_name ?? '').toLowerCase().includes(q)) return true;
      if ((it.item_category ?? '').toLowerCase().includes(q)) return true;
      if ((it.tags ?? '').toLowerCase().includes(q)) return true;
      return false;
    });
  })();
  const total = cache?.total ?? 0;
  const loading = cache?.loading ?? false;

  // FE-203 — hand-back action when the viewer is the current
  // custodian of an item (will be wired once BE-209 ships
  // current_custodian_user_id on GroupItemSummary). Today this
  // helper is a no-op placeholder reused only by the partial-state
  // CTA logic below.
  const handleHandBack = useCallback(
    async (item: GroupItemSummary) => {
      if (handingBackId != null) return;
      setHandingBackId(item.item_id);
      try {
        const result = await dispatch(
          returnItem({ itemId: item.item_id }) as any,
        );
        if (returnItem.fulfilled.match(result)) {
          Toast.show({
            type: 'success',
            text1: 'Returned',
            text2: `${item.item_name ?? 'Item'} returned to owner`,
          });
          // Refresh so the row's holder + CTA reflect the new state.
          dispatch(
            loadGroupInventory({
              groupId,
              limit: PAGE_SIZE,
              offset: 0,
              mode: 'replace',
            }),
          );
        } else {
          Toast.show({
            type: 'error',
            text1: 'Return failed',
            text2:
              (result.payload as string) ?? 'Please try again.',
          });
        }
      } finally {
        setHandingBackId(null);
      }
    },
    [dispatch, groupId, handingBackId],
  );

  // FE #198 / Sub 1 — Custodian acknowledges an active recall straight
  // from the crew inventory row. Reuses the in-flight indicator slot
  // (`handingBackId`) so we don't introduce a parallel state machine:
  // both actions are mutually exclusive (server rejects ack on a row
  // that's already mid-return). On success, the slice's bulk reload
  // flips `has_active_recall` false → red variant disappears + the
  // row drops out of Blair's group view (custody returned to owner).
  const handleAcknowledgeRecall = useCallback(
    async (item: GroupItemSummary) => {
      if (handingBackId != null) return;
      setHandingBackId(item.item_id);
      try {
        await api.acknowledgeRecall(item.item_id);
        Toast.show({
          type: 'success',
          text1: 'Recall acknowledged',
          text2: `${item.item_name ?? 'Item'} returned to owner`,
        });
        dispatch(
          loadGroupInventory({
            groupId,
            limit: PAGE_SIZE,
            offset: 0,
            mode: 'replace',
          }),
        );
      } catch (err: any) {
        Toast.show({
          type: 'error',
          text1: "Couldn't acknowledge",
          text2:
            (err?.response?.data?.detail || err?.message) ??
            'Please try again.',
        });
      } finally {
        setHandingBackId(null);
      }
    },
    [dispatch, groupId, handingBackId],
  );
  const error = cache?.error ?? null;
  const hasLoaded = cache?.hasLoaded ?? false;
  const nextOffset = cache?.nextOffset ?? 0;
  const hasMore = items.length < total;

  // Initial load — fire on mount if we've never loaded this group's
  // inventory before. Re-fetching on every mount would flash an old
  // page; we trust the cache and let the user pull-to-refresh.
  useEffect(() => {
    if (!hasLoaded && !loading) {
      dispatch(
        loadGroupInventory({ groupId, limit: PAGE_SIZE, offset: 0, mode: 'replace' }),
      );
    }
    // We intentionally only react to groupId changing — remounts of
    // this component on the same group should NOT re-fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  /* ── handlers ──────────────────────────────────────────────────── */

  const handleRefresh = useCallback(() => {
    dispatch(
      loadGroupInventory({ groupId, limit: PAGE_SIZE, offset: 0, mode: 'replace' }),
    );
  }, [dispatch, groupId]);

  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    dispatch(
      loadGroupInventory({
        groupId,
        limit: PAGE_SIZE,
        offset: nextOffset,
        mode: 'append',
      }),
    );
  }, [dispatch, groupId, loading, hasMore, nextOffset]);

  const handleOpenItem = useCallback(
    (item: GroupItemSummary) => {
      // Navigate to ItemDetail with the group context attached so
      // CustodySection (Sprint 3 / Chunk 10A) knows which crew this
      // item is being viewed from. We pass `itemId` as the lookup key
      // and `primaryGroupId` so the handoff sheet can pre-fill group_id.
      navigation.navigate('ItemDetail', {
        itemId: String(item.item_id),
        primaryGroupId: groupId,
      });
    },
    [navigation, groupId],
  );

  const handleRemove = useCallback(
    (item: GroupItemSummary) => {
      Alert.alert(
        `Remove from ${groupName}?`,
        `${item.item_name ?? 'This item'} will stay in your inventory but won't be visible to ${groupName} members.`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Remove',
            style: 'destructive',
            onPress: () => {
              dispatch(removeItemFromGroup({ groupId, itemId: item.item_id }));
            },
          },
        ],
        { cancelable: true },
      );
    },
    [dispatch, groupId, groupName],
  );

  /* ── derived ───────────────────────────────────────────────────── */

  const headerCount = useMemo(() => {
    if (!hasLoaded) return null;
    return total === 0
      ? '0 items'
      : total === 1
      ? '1 item'
      : `${total} items`;
  }, [hasLoaded, total]);

  /* ── render ────────────────────────────────────────────────────── */

  return (
    <View style={styles.section}>
      {/* FE-248 v2 — sticky summary banner. Renders only when >5 items
          in this group have pending requests for the viewer. Below
          that threshold the inline strips are enough. */}
      {pendingSummary.itemsWithPending > 5 ? (
        <InventoryPendingSummaryBanner
          groupId={groupId}
          itemsWithPending={pendingSummary.itemsWithPending}
          totalRequests={pendingSummary.totalRequests}
          expiringSoonCount={pendingSummary.expiringSoonCount}
        />
      ) : null}
      <View style={styles.headerRow}>
        <Text style={styles.sectionLabel}>Inventory</Text>
        {headerCount !== null && (
          <Text style={styles.headerCount}>{headerCount}</Text>
        )}
        {canAdd ? (
          <Pressable
            onPress={() => setAddSheetOpen(true)}
            accessibilityRole="button"
            accessibilityLabel="Add items to this circle"
            style={({ pressed }) => [
              styles.addBtn,
              pressed && { opacity: 0.85 },
            ]}
            testID="group-add-items-btn"
          >
            <MaterialIcons name="add" size={16} color="#FFFFFF" />
            <Text style={styles.addBtnText}>Add items</Text>
          </Pressable>
        ) : null}
      </View>

      {/* Search-UX §3.6 — per-crew item filter.  Local-only over the
          already-loaded set (Notion's per-database search precedent);
          scope is spelled out in the placeholder so users never
          wonder "is this searching everything or just this crew?".
          Hidden until the inventory has loaded at least once so the
          input doesn't render above an empty skeleton. */}
      {hasLoaded && rawItems.length > 0 && (
        <View style={styles.searchWrap}>
          <MaterialIcons
            name="search"
            size={16}
            color={fpColors.outline}
            style={{ marginRight: 6 }}
          />
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder={`Search items in ${groupName}`}
            placeholderTextColor={fpColors.outline}
            style={styles.searchInput}
            autoCorrect={false}
            autoCapitalize="none"
            accessibilityLabel={`Search items in ${groupName}`}
            testID="group-items-search"
          />
          {searchQuery.length > 0 && (
            <Pressable
              onPress={() => setSearchQuery('')}
              hitSlop={6}
              accessibilityRole="button"
              accessibilityLabel="Clear item search"
              testID="group-items-search-clear"
            >
              <MaterialIcons name="close" size={16} color={fpColors.outline} />
            </Pressable>
          )}
        </View>
      )}

      {/* Initial load: skeleton */}
      {!hasLoaded && loading && (
        <View style={styles.skeletonWrap}>
          {[0, 1, 2, 3].map((i) => (
            <View key={i} style={styles.skeletonRow} />
          ))}
        </View>
      )}

      {/* Error banner with retry */}
      {error && (
        <View style={styles.errorBanner}>
          <MaterialIcons
            name="error-outline"
            size={18}
            color={fpColors.error ?? '#b00020'}
            style={{ marginRight: 8 }}
          />
          <Text style={styles.errorText}>{error}</Text>
          <Pressable
            onPress={handleRefresh}
            accessibilityRole="button"
            accessibilityLabel="Retry loading inventory"
            style={styles.errorRetry}
          >
            <Text style={styles.errorRetryText}>Retry</Text>
          </Pressable>
        </View>
      )}

      {/* Empty state */}
      {hasLoaded && !error && items.length === 0 && (
        <View style={styles.emptyWrap}>
          <Text style={styles.emptyEmoji}>📦</Text>
          <Text style={styles.emptyTitle}>No items yet</Text>
          <Text style={styles.emptyBody}>
            Bind items to {groupName} so anyone in the circle can see + borrow them.
          </Text>
          {canAdd ? (
            <Pressable
              onPress={() => setAddSheetOpen(true)}
              accessibilityRole="button"
              accessibilityLabel="Add items to this circle"
              style={({ pressed }) => [
                styles.emptyCta,
                pressed && { opacity: 0.85 },
              ]}
              testID="group-empty-add-items-btn"
            >
              <MaterialIcons name="add" size={18} color="#FFFFFF" />
              <Text style={styles.emptyCtaText}>Add items</Text>
            </Pressable>
          ) : null}
        </View>
      )}

      {/* Items */}
      {items.length > 0 && (
        <View>
          {items.map((it) => {
            const ownerId = (it as any).user_id as number | null | undefined;
            // FE-203 — pending BE-209: GroupItemSummary doesn't carry
            // current_custodian_user_id yet, so the CTA is binary
            // (With you vs Borrow). When BE-209 lands and exposes
            // custodian + due_date, the row will gain a third
            // [Hand back] state + an overdue indicator.
            const viewerIsOwner =
              viewerId != null && ownerId != null && ownerId === viewerId;
            const custodianId = (it as any).current_custodian_user_id as
              | number
              | null
              | undefined;
            const viewerIsCustodian =
              custodianId != null && viewerId === custodianId;
            const holderName =
              custodianId == null
                ? null
                : custodianId === viewerId
                  ? 'you'
                  : memberNameById[custodianId] ?? null;
            const ownerName =
              ownerId == null
                ? null
                : ownerId === viewerId
                  ? null /* hide "owned by you" — implied */
                  : memberNameById[ownerId] ?? null;
            const viewerHasPendingRequest = pendingRequestItemIds.has(
              it.item_id,
            );
            // FE-248 v2 — inline strip below the row when this item has
            // 1+ PENDING/WAITLISTED requests addressed to the viewer
            // (the selector already scopes to current user's items via
            // server-side filtering on receivedRequests).
            const itemPending = pendingByItem.get(it.item_id);
            return (
              <View key={it.item_id}>
                <GroupInventoryRow
                  item={it}
                  canRemove={isAdmin}
                  viewerIsOwner={viewerIsOwner}
                  viewerIsCustodian={viewerIsCustodian}
                  viewerHasPendingRequest={viewerHasPendingRequest}
                  holderName={holderName}
                  ownerName={ownerName}
                  handingBack={handingBackId === it.item_id}
                  onRemove={() => handleRemove(it)}
                  onOpen={() => handleOpenItem(it)}
                  onBorrow={() => setBorrowFor(it)}
                  onHandBack={() => handleHandBack(it)}
                  onAcknowledgeRecall={() => handleAcknowledgeRecall(it)}
                />
                {itemPending && itemPending.length > 0 ? (
                  <InventoryRowPendingStrip requests={itemPending} />
                ) : null}
              </View>
            );
          })}

          {/* Pagination footer */}
          {hasMore && (
            <Pressable
              onPress={handleLoadMore}
              accessibilityRole="button"
              accessibilityLabel="Load more items"
              disabled={loading}
              style={({ pressed }) => [
                styles.loadMore,
                pressed && { opacity: 0.85 },
                loading && { opacity: 0.55 },
              ]}
            >
              {loading ? (
                <ActivityIndicator color={fpColors.primary} />
              ) : (
                <Text style={styles.loadMoreText}>
                  Load {Math.min(PAGE_SIZE, total - items.length)} more
                </Text>
              )}
            </Pressable>
          )}
          {!hasMore && total > 0 && (
            <Text style={styles.allLoaded}>
              {`Showing ${items.length} of ${total}`}
            </Text>
          )}
        </View>
      )}

      {/* Sprint 3 polish (FE-10.1.5) — pick existing items to bind to
          this group. Replaces the "scan-time only" limitation. */}
      <AddItemsToGroupSheet
        visible={addSheetOpen}
        groupId={groupId}
        groupName={groupName}
        onClose={() => setAddSheetOpen(false)}
        onAdded={() => {
          // Refetch the group inventory so the new rows appear.
          dispatch(
            loadGroupInventory({
              groupId,
              limit: PAGE_SIZE,
              offset: 0,
              mode: 'replace',
            }),
          );
        }}
      />

      {/* FE-203 — Borrow-request modal. Mounts only when a row's
          [Borrow] is tapped (gated on `borrowFor !== null` so it
          doesn't subscribe state when closed). On success we refresh
          the inventory so the CTA flips ([Borrow] → [Hand back] when
          BE-209 lands; until then just refreshes the page). */}
      {borrowFor ? (
        <RequestHandoffSheet
          visible
          itemId={borrowFor.item_id}
          itemName={borrowFor.item_name ?? 'this item'}
          groupId={groupId}
          ownerUserId={(borrowFor as any).user_id ?? null}
          onClose={() => setBorrowFor(null)}
          onSuccess={() => {
            setBorrowFor(null);
            dispatch(
              loadGroupInventory({
                groupId,
                limit: PAGE_SIZE,
                offset: 0,
                mode: 'replace',
              }),
            );
            // FE-220 hotfix — refresh outgoing borrow-requests so the
            // [Borrow] CTA on this row flips to "Requested" immediately
            // (cache had no entry for this item_id pre-submit; without
            // a refetch the row stays on [Borrow] until the next mount).
            dispatch(loadSentRequests() as any);
          }}
        />
      ) : null}
    </View>
  );
};

/* ── row component ────────────────────────────────────────────────── */

interface GroupInventoryRowProps {
  item: GroupItemSummary;
  canRemove: boolean;
  /** FE-203 — `viewerIsOwner` and `viewerIsCustodian` drive the CTA
   *  state machine + the "owned by X / with X" subtitle. Both come
   *  from the parent so it can centralize the member-cache lookup
   *  + the viewerId comparison. */
  viewerIsOwner: boolean;
  viewerIsCustodian: boolean;
  /** FE-220 hotfix — true when the viewer has an outgoing PENDING /
   *  WAITLISTED borrow request on this item. Swaps the [Borrow] pill
   *  to a disabled "Requested" pill so the user can't fire duplicates
   *  (which would notify the owner twice and clutter their inbox). */
  viewerHasPendingRequest?: boolean;
  /** Display name of the holder (when not the viewer). Null when
   *  the row has no custodian info (pending BE-209) or when the
   *  viewer is the holder. */
  holderName: string | null;
  /** Display name of the owner (when not the viewer). Null when the
   *  viewer IS the owner. */
  ownerName: string | null;
  /** Currently dispatching the hand-back thunk — disables the CTA. */
  handingBack: boolean;
  onRemove: () => void;
  /** Tap the row body → open ItemDetail (with group context). */
  onOpen?: () => void;
  /** Tap [Borrow] → opens the RequestHandoffSheet for this item. */
  onBorrow?: () => void;
  /** Tap [Hand back] → fires returnItem on this item. */
  onHandBack?: () => void;
  /** FE #198 / Sub 1 — Tap [Acknowledge & return] on a row with an
   *  active recall → fires api.acknowledgeRecall. Optional because
   *  the parent screen wires it conditionally (only renders for
   *  groups where the viewer might be a custodian). */
  onAcknowledgeRecall?: () => void;
}

const GroupInventoryRow: React.FC<GroupInventoryRowProps> = ({
  item,
  canRemove,
  viewerIsOwner,
  viewerIsCustodian,
  viewerHasPendingRequest = false,
  holderName,
  ownerName,
  handingBack,
  onRemove,
  onOpen,
  onBorrow,
  onHandBack,
  onAcknowledgeRecall,
}) => {
  const value = item.estimated_value;
  // FE-202 — Comma-separate large values so $5500 renders as $5,500
  // instead of $5500 (which truncates as $55... when crowded into the
  // single-line meta row). `toLocaleString` with currency formatting
  // also strips the .00 for whole-dollar values, saving width.
  const valueLabel =
    typeof value === 'number'
      ? value.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        })
      : null;
  const addedAt = useMemo(() => {
    try {
      const d = new Date(item.added_at);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return null;
    }
  }, [item.added_at]);

  // FE-203 — CTA decision tree.
  //  - viewerIsCustodian && !viewerIsOwner → [Hand back] (will need
  //    BE-209's current_custodian_user_id to fire correctly; falls
  //    back to no CTA when custodian info is unavailable)
  //  - viewerIsOwner && (no custodian or custodian == viewer) → [With you]
  //  - everything else → [Borrow] (other-member's item, or pending
  //    BE-209 we default to inviting a borrow request)
  // FE-220 hotfix — when the item is in a state where it can't be
  // borrowed right now (active 1-on-1 share, currently held by someone
  // outside the group, owner has issued recall), hide the Borrow CTA
  // for non-owner viewers. The availability_state chip already tells
  // them WHY; the CTA disappearing is the natural follow-on so they
  // don't tap into a doomed handoff-request.
  const unavailable =
    item.availability_state === 'shared' ||
    item.availability_state === 'lent_out' ||
    item.availability_state === 'recalled';
  // FE-220 hotfix — `requested` slots between `borrow` and `none`: the
  // item is borrowable in principle, but the viewer has an open request
  // pending owner action. Render a disabled "Requested" pill so we
  // don't fire duplicate handoff-requests on each tap.
  //
  // FE-248 v2 follow-up — `lent-out` for the owner-whose-item-is-out
  // case. Without it the CTA fell through to 'with-you' even though
  // the item was physically held by another user, which made the
  // owner think their item was available when it wasn't.
  const rowCustodianId = (item as any).current_custodian_user_id as
    | number
    | null
    | undefined;
  const itemIsOutWithSomeoneElse =
    (rowCustodianId != null && !viewerIsCustodian) ||
    item.availability_state === 'lent_out' ||
    item.availability_state === 'in_use_by_member';
  // FE #198 / Sub 1 — Recall variant takes precedence over the regular
  // hand-back path when (a) the viewer is the custodian and (b) the
  // item has an active recall. Owner-side view stays unchanged — the
  // owner already sees the recall via the "Recall pending · you sent
  // this" row from FE #210 and doesn't need this red affordance.
  const hasActiveRecall = (item as any).has_active_recall === true;
  const showRecallVariant =
    hasActiveRecall && viewerIsCustodian && !viewerIsOwner;
  const ctaState:
    | 'with-you'
    | 'hand-back'
    | 'borrow'
    | 'requested'
    | 'lent-out'
    | 'recall-ack'
    | 'none' = showRecallVariant
    ? 'recall-ack'
    : viewerIsCustodian
    ? viewerIsOwner
      ? 'with-you'
      : 'hand-back'
    : viewerIsOwner
      ? itemIsOutWithSomeoneElse
        ? 'lent-out'
        : 'with-you'
      : unavailable
        ? 'none'
        : viewerHasPendingRequest
          ? 'requested'
          : 'borrow';

  // Subtitle parts: holder context (when known) + metadata.
  // Examples:
  //   "with Marcus · power tool · $310 · added Apr 28"
  //   "owned by you · measurement · $620"
  //   "owned by Marcus · ..." (when current custodian unknown)
  const holderLine = holderName
    ? holderName === 'you'
      ? 'with you'
      : `with ${holderName}`
    : ownerName
      ? `owned by ${ownerName}`
      : null;

  // FE-220 / SG-1 + SG-2 — Per-row availability indicator. Renders ONE
  // of five chips below the row body, color-coded so the user sees
  // at-a-glance state without having to read the meta line.
  //
  //  - 🟢 Available           : owner holds, no active share
  //  - 🟠 Lent out · {name}   : owner viewing OR custodian is in-group
  //                              (SG-2 leaks the id only when allowed)
  //  - 🟠 Out with another    : custodian hidden per SG-2 (non-owner,
  //    member                   custodian not in this group)
  //  - 🟣 Shared              : 1-on-1 share active
  //  - ⚠ Recall pending      : owner has issued /recall
  //
  // Defaults to null when BE didn't return availability_state — keeps
  // the row visually identical to today for backwards compat (e.g.,
  // legacy responses pre-rev-3).
  const state = item.availability_state;
  type AvailabilityPill = {
    icon: keyof typeof MaterialIcons.glyphMap;
    label: string;
    tone: 'available' | 'out' | 'shared' | 'recalled';
  };
  const availability: AvailabilityPill | null = (() => {
    // FE #209 — when the viewer has a pending borrow request on this row,
    // the CTA already renders the muted "Requested" pill. Showing
    // "Available" alongside it is contradictory: from the viewer's POV
    // this item is NOT available (they're waiting on owner approval).
    // Suppress the "Available" chip and let the Requested CTA speak.
    if (ctaState === 'requested') {
      return null;
    }
    if (!state || state === 'available') {
      return state === 'available'
        ? ({ icon: 'check-circle', label: 'Available', tone: 'available' } as AvailabilityPill)
        : null;
    }
    if (state === 'shared') {
      return { icon: 'public', label: 'Shared', tone: 'shared' } as AvailabilityPill;
    }
    if (state === 'recalled') {
      return { icon: 'warning', label: 'Recall pending', tone: 'recalled' } as AvailabilityPill;
    }
    // 'lent_out' or 'in_use_by_member' fall here.
    //
    // FE-248 v2 follow-up — three branches:
    //
    //   1. viewerIsCustodian → "With you" (the viewer IS holding it).
    //      Previously fell through to "Out with another member" because
    //      the `holderName !== 'you'` check filtered the viewer out.
    //
    //   2. holderName resolves to a real name → "Lent out · {name} · Nd"
    //
    //   3. Custodian id stripped (SG-2 hide OR backend#247 bug) → fall
    //      back to the generic "Out with another member" copy. When
    //      this fires for the OWNER it indicates a BE bug since SG-2
    //      should expose the id to the owner — filed as backend#247.
    if (holderName === 'you') {
      return {
        icon: 'check-circle',
        label: 'With you',
        tone: 'available',
      };
    }
    if (holderName && holderName !== 'you') {
      // Gap #111 (2026-05-27 audit) — formatting was `${days}d` which
      // produced "0d" for handoffs <24h old. That reads as "zero days,
      // didn't happen" instead of "today". Switch to a relative-short
      // formatter that handles the <24h / 1d / Nd buckets.
      const relativeShort = item.custody_since
        ? formatRelativeShort(new Date(item.custody_since).getTime())
        : null;
      return {
        icon: 'south-west',
        label:
          relativeShort != null
            ? `Lent out · ${holderName} · ${relativeShort}`
            : `Lent out · ${holderName}`,
        tone: 'out',
      };
    }
    // Last resort: try the BE-denormalized `owner_name` on the row
    // when the item is held by the owner from the viewer's perspective
    // (the rare case where viewer is NOT the owner but the row still
    // says lent_out — generic copy keeps it honest).
    return {
      icon: 'south-west',
      label: 'Out with another member',
      tone: 'out',
    };
  })();

  // Gap #109 (2026-05-27 audit) — when the row is in recall-state the
  // CTA is "Acknowledge & return" which is wider than the regular
  // Borrow chip and squeezes the text column hard enough to truncate
  // the item name + price. Stack the CTA below the row body on this
  // state ONLY; every other ctaState keeps the side-by-side layout.
  const isRecallCta = ctaState === 'recall-ack';

  return (
    <Pressable
      onPress={onOpen}
      disabled={!onOpen}
      accessibilityRole={onOpen ? 'button' : undefined}
      accessibilityLabel={`Open ${item.item_name ?? 'item'}`}
      style={({ pressed }) => [
        styles.row,
        showRecallVariant && styles.rowRecall,
        isRecallCta && styles.rowRecallStacked,
        pressed && onOpen ? { opacity: 0.7 } : null,
      ]}
      testID={`group-inv-row-${item.item_id}`}
    >
      <View style={isRecallCta ? styles.rowRecallTop : styles.rowInline}>
      {/* FE-256 — fallback chain: primary_photo_url → source_image_url
          → image_url → item_image_url. Fixes the bug where legacy
          items (no primary_photo_url) showed a placeholder here but
          a thumbnail on the Items grid. */}
      <ItemThumbnail item={item as any} size={44} />

      <View style={styles.rowText}>
        {/* FE-202 — numberOfLines={2} prevents mid-word truncation on
            longer item names ("Workshop Power Tools Set", "Sony FX3
            Cinema Camera + Kit") on the iPhone 13 (390px) viewport.
            Adds ~16px of vertical real estate per row which is fine
            because the dropped `category` from the meta line below
            offsets it. */}
        <Text style={styles.rowName} numberOfLines={2}>
          {item.item_name ?? 'Unnamed item'}
        </Text>
        <Text style={styles.rowMeta} numberOfLines={1}>
          {[
            holderLine,
            // FE-202 — Category dropped from the meta line. It was
            // redundant with the visual context (rowed under group)
            // and crowded out price + holder + added-on, which are
            // the fields the borrower actually needs to decide
            // whether to tap.
            valueLabel,
            addedAt && `added ${addedAt}`,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        {/* FE-220 / SG-1 — availability_state chip. Hidden when BE
            didn't populate the field (pre-rev-3 legacy responses) so
            the row stays visually identical to today. */}
        {availability ? (
          <View
            style={[
              styles.availChip,
              availability.tone === 'available' && styles.availChipAvailable,
              availability.tone === 'out' && styles.availChipOut,
              availability.tone === 'shared' && styles.availChipShared,
              availability.tone === 'recalled' && styles.availChipRecalled,
            ]}
            accessibilityLabel={availability.label}
            testID={`group-inv-avail-${item.item_id}`}
          >
            {(() => {
              // FE-220 hotfix — text + icon use the `on*Container`
              // foreground for proper contrast against the
              // *Container background. Using the *primary* family
              // colors (which the prior version did) put text in
              // the same color family as the background → unreadable.
              const fg =
                availability.tone === 'available'
                  ? fpColors.onPrimaryContainer
                  : availability.tone === 'recalled'
                    ? fpColors.onErrorContainer
                    : availability.tone === 'shared'
                      ? fpColors.onTertiaryContainer
                      : fpColors.onSurface;
              return (
                <>
                  <MaterialIcons
                    name={availability.icon}
                    size={11}
                    color={fg}
                  />
                  <Text
                    style={[styles.availChipText, { color: fg }]}
                    numberOfLines={1}
                  >
                    {availability.label}
                  </Text>
                </>
              );
            })()}
          </View>
        ) : null}
      </View>
      {/* Gap #109 — close the wrapper that holds thumb + rowText. For
          non-recall states the CTA (below) becomes the wrapper's right
          sibling, restoring the original side-by-side row layout. */}
      </View>

      {/* FE-203 — state-aware CTA. We render to the right of the row
          body and stop propagation so a tap on the CTA doesn't also
          open ItemDetail. Gap #109 — recall-state CTA is rendered
          below this block as a full-width stacked button. */}
      {ctaState === 'with-you' ? (
        <View
          style={styles.ctaChipReadOnly}
          accessibilityLabel="You currently hold this item"
          testID={`group-inv-cta-withyou-${item.item_id}`}
        >
          <MaterialIcons
            name="check-circle"
            size={14}
            color={fpColors.primary}
          />
          <Text style={styles.ctaChipReadOnlyText}>With you</Text>
        </View>
      ) : ctaState === 'hand-back' ? (
        <Pressable
          onPress={(e) => {
            if (typeof (e as any)?.stopPropagation === 'function') {
              (e as any).stopPropagation();
            }
            onHandBack?.();
          }}
          disabled={handingBack || !onHandBack}
          hitSlop={6}
          style={({ pressed }) => [
            styles.ctaBtn,
            styles.ctaBtnSecondary,
            (handingBack || !onHandBack) && { opacity: 0.55 },
            pressed && { opacity: 0.85 },
          ]}
          accessibilityRole="button"
          accessibilityLabel={`Hand back ${item.item_name ?? 'this item'} to its owner`}
          testID={`group-inv-cta-handback-${item.item_id}`}
        >
          {handingBack ? (
            <ActivityIndicator size="small" color={fpColors.primary} />
          ) : (
            <>
              <MaterialIcons
                name="reply"
                size={14}
                color={fpColors.primary}
              />
              <Text style={styles.ctaBtnSecondaryText}>Hand back</Text>
            </>
          )}
        </Pressable>
      ) : ctaState === 'borrow' ? (
        <Pressable
          onPress={(e) => {
            if (typeof (e as any)?.stopPropagation === 'function') {
              (e as any).stopPropagation();
            }
            onBorrow?.();
          }}
          disabled={!onBorrow}
          hitSlop={6}
          style={({ pressed }) => [
            styles.ctaBtn,
            !onBorrow && { opacity: 0.55 },
            pressed && { opacity: 0.85 },
          ]}
          accessibilityRole="button"
          accessibilityLabel={`Borrow ${item.item_name ?? 'this item'}`}
          testID={`group-inv-cta-borrow-${item.item_id}`}
        >
          <MaterialIcons
            name="swap-horiz"
            size={14}
            color={fpColors.onPrimary}
          />
          <Text style={styles.ctaBtnText}>Borrow</Text>
        </Pressable>
      ) : ctaState === 'requested' ? (
        // FE-220 hotfix — viewer has an outgoing PENDING/WAITLISTED
        // borrow-request for this row. Disabled "Requested" pill so the
        // user can't fire duplicate requests (which would notify the
        // owner once per tap and clutter their inbox).
        <View
          style={[styles.ctaBtn, styles.ctaBtnRequested]}
          accessibilityRole="text"
          accessibilityLabel={`Borrow request pending for ${item.item_name ?? 'this item'}`}
          testID={`group-inv-cta-requested-${item.item_id}`}
        >
          <MaterialIcons
            name="hourglass-empty"
            size={14}
            color={fpColors.onPrimaryContainer ?? fpColors.primary}
          />
          <Text style={styles.ctaBtnRequestedText}>Requested</Text>
        </View>
      ) : ctaState === 'lent-out' ? (
        // FE-248 v2 — owner whose item is currently held by another
        // member. Render a muted "Lent out" chip so the owner sees
        // clearly that the item isn't available right now (the
        // availability_state pill below the row says with whom). Tap
        // navigates to ItemDetail where Send Reminder / Recall live.
        <Pressable
          onPress={(e) => {
            if (typeof (e as any)?.stopPropagation === 'function') {
              (e as any).stopPropagation();
            }
            onOpen?.();
          }}
          style={({ pressed }) => [
            styles.ctaBtn,
            styles.ctaBtnLentOut,
            pressed && { opacity: 0.85 },
          ]}
          accessibilityRole="button"
          accessibilityLabel={`Lent out — open ${item.item_name ?? 'item'} to send a reminder or recall`}
          testID={`group-inv-cta-lentout-${item.item_id}`}
        >
          <MaterialIcons
            name="south-west"
            size={14}
            color={
              (fpColors as any).onTertiaryContainer ?? fpColors.primary
            }
          />
          <Text style={styles.ctaBtnLentOutText}>Lent out</Text>
        </Pressable>
      ) : (
        // FE-220 hotfix — `none` state: item is shared / lent out /
        // recalled and the non-owner can't request a borrow right now.
        // Render an empty fixed-width slot so other rows still align;
        // the availability_state chip already tells the user why.
        <View style={styles.ctaPlaceholder} testID={`group-inv-cta-none-${item.item_id}`} />
      )}

      {/* Gap #109 — recall-state CTA rendered full-width below the row
          body (only when isRecallCta). Same handler + same accessibility
          contract as the inline variant it replaces. */}
      {isRecallCta && (
        <Pressable
          onPress={(e) => {
            if (typeof (e as any)?.stopPropagation === 'function') {
              (e as any).stopPropagation();
            }
            onAcknowledgeRecall?.();
          }}
          disabled={handingBack || !onAcknowledgeRecall}
          hitSlop={6}
          style={({ pressed }) => [
            styles.ctaBtn,
            styles.ctaBtnRecall,
            styles.ctaBtnRecallStacked,
            (handingBack || !onAcknowledgeRecall) && { opacity: 0.55 },
            pressed && { opacity: 0.85 },
          ]}
          accessibilityRole="button"
          accessibilityLabel={`Acknowledge recall and return ${item.item_name ?? 'this item'}`}
          testID={`group-inv-cta-recall-ack-${item.item_id}`}
        >
          {handingBack ? (
            <ActivityIndicator
              size="small"
              color={(fpColors as any).onError ?? '#FFFFFF'}
            />
          ) : (
            <>
              <MaterialIcons
                name="priority-high"
                size={14}
                color={(fpColors as any).onError ?? '#FFFFFF'}
              />
              <Text style={styles.ctaBtnRecallText}>Acknowledge & return</Text>
            </>
          )}
        </Pressable>
      )}

      {canRemove && (
        <Pressable
          onPress={(e) => {
            // Prevent the parent row's onPress from firing — only the
            // remove action should trigger. RN's synthetic event has
            // optional `stopPropagation`; guard for environments
            // (incl. RNTL) that don't expose it.
            if (typeof (e as any)?.stopPropagation === 'function') {
              (e as any).stopPropagation();
            }
            onRemove();
          }}
          accessibilityRole="button"
          accessibilityLabel={`Remove ${item.item_name ?? 'item'} from this circle`}
          hitSlop={8}
          style={({ pressed }) => [
            styles.removeBtn,
            pressed && { opacity: 0.7 },
          ]}
        >
          <MaterialIcons
            name="remove-circle-outline"
            size={20}
            color={fpColors.outline}
          />
        </Pressable>
      )}
    </Pressable>
  );
};

/* ── styles ──────────────────────────────────────────────────────── */

const styles = StyleSheet.create({
  section: {
    marginTop: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    marginBottom: 8,
    gap: 12,
  },
  // Search-UX §3.6 — per-crew item filter input.  Lives between the
  // section header and the items grid so it scrolls with the list.
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 16,
    marginBottom: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
    padding: 0,
    margin: 0,
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: fpColors.onSurface,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  headerCount: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    flex: 1,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: fpColors.primary,
    borderRadius: 16,
  },
  addBtnText: {
    fontFamily: 'Inter_600SemiBold',
    color: '#FFFFFF',
    fontSize: 13,
  },
  skeletonWrap: {
    paddingHorizontal: 16,
  },
  skeletonRow: {
    height: 60,
    backgroundColor: fpColors.surfaceContainerLow,
    borderRadius: 8,
    marginBottom: 8,
    opacity: 0.6,
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginHorizontal: 16,
    backgroundColor: '#ffebee',
    borderRadius: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 13,
    color: fpColors.onSurface,
  },
  errorRetry: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    backgroundColor: fpColors.primary,
    borderRadius: 6,
  },
  errorRetryText: {
    fontSize: 12,
    color: fpColors.onPrimary,
    fontWeight: '600',
  },
  emptyWrap: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    alignItems: 'center',
  },
  emptyEmoji: {
    fontSize: 36,
    marginBottom: 8,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: fpColors.onSurface,
    marginBottom: 4,
  },
  emptyBody: {
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    textAlign: 'center',
    lineHeight: 18,
  },
  emptyCta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 16,
    paddingHorizontal: 18,
    paddingVertical: 10,
    backgroundColor: fpColors.primary,
    borderRadius: 12,
  },
  emptyCtaText: {
    fontFamily: 'Inter_600SemiBold',
    color: '#FFFFFF',
    fontSize: 14,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  // FE #198 / Sub 1 — Red urgent tint on rows where the viewer is
  // the custodian AND there's an active recall. The accent draws
  // the eye to the row at the crew-overview level before the user
  // even taps in; the [Acknowledge & return] CTA finishes the job.
  rowRecall: {
    backgroundColor: (fpColors as any).errorContainer ?? '#FFDAD6',
    borderLeftWidth: 3,
    borderLeftColor: fpColors.error,
  },
  // Gap #109 (2026-05-27 audit) — When ctaState === 'recall-ack' we
  // switch the row's flex axis to column so the [Acknowledge & return]
  // CTA can render below the row body at full width without squeezing
  // the item name + price column.
  rowRecallStacked: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  // Gap #109 — wrapper around thumbnail + rowText. Non-recall states
  // use `rowInline` (flex: 1 to share space with the inline CTA on
  // the right). Recall state uses `rowRecallTop` (full width — the
  // CTA sits below the wrapper, not beside it).
  rowInline: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  rowRecallTop: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  // FE #198 / Sub 1 — The red CTA variant. Differs from `ctaBtn` (the
  // primary purple "Borrow" button) in that it always uses `error`
  // family colors regardless of theme.
  ctaBtnRecall: {
    backgroundColor: fpColors.error,
  },
  // Gap #109 — when the recall CTA is stacked below the row, override
  // ctaBtn's `minWidth: 80` and let it span the row's full width.
  ctaBtnRecallStacked: {
    marginTop: 10,
    width: '100%',
    minWidth: 0,
  },
  ctaBtnRecallText: {
    color: (fpColors as any).onError ?? '#FFFFFF',
  },
  thumb: {
    width: 44,
    height: 44,
    borderRadius: 8,
    marginRight: 12,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  thumbPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowText: {
    flex: 1,
  },
  rowName: {
    fontSize: 14,
    fontWeight: '600',
    color: fpColors.onSurface,
  },
  rowMeta: {
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  // FE-220 / SG-1 — availability_state chip styles. Four tones so the
  // row's state is readable at a glance without parsing the meta line.
  availChip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
    marginTop: 4,
  },
  // FE-220 — chip backgrounds. Paired with `on*Container` text below
  // so contrast is WCAG-AA across all four tones. Originally text used
  // primary/tertiary/etc colors which sit on the SAME color family as
  // the background → invisible. Hotfix swaps to on*Container.
  availChipAvailable: {
    backgroundColor: fpColors.primaryContainer,
  },
  availChipOut: {
    backgroundColor: fpColors.surfaceContainerHighest,
  },
  availChipShared: {
    backgroundColor: fpColors.tertiaryContainer,
  },
  availChipRecalled: {
    backgroundColor: fpColors.errorContainer,
  },
  availChipText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 10,
    color: fpColors.onSurface, // default — overridden per-tone in JSX
    letterSpacing: 0.1,
  },
  // FE-220 hotfix — empty CTA slot when borrow isn't possible.
  // Fixed dimensions so the row layout stays consistent.
  ctaPlaceholder: {
    width: 1,
    height: 28,
  },
  removeBtn: {
    paddingLeft: 8,
    paddingVertical: 4,
  },
  // FE-203 — state-aware CTA styles. Three variants:
  //   ctaChipReadOnly  → "With you" non-pressable chip
  //   ctaBtn (primary) → "Borrow" filled pill
  //   ctaBtnSecondary  → "Hand back" outline pill
  ctaChipReadOnly: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: fpColors.primaryContainer,
  },
  ctaChipReadOnlyText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.primary,
    letterSpacing: 0.2,
  },
  ctaBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: fpColors.primary,
    minWidth: 80,
    justifyContent: 'center',
  },
  ctaBtnText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onPrimary,
    letterSpacing: 0.2,
  },
  ctaBtnSecondary: {
    backgroundColor: fpColors.surface,
    borderWidth: 1,
    borderColor: fpColors.primary,
  },
  ctaBtnSecondaryText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.primary,
    letterSpacing: 0.2,
  },
  // FE-220 hotfix — "Requested" pill: muted container so it reads as
  // inert / awaiting next to a live primary pill. Same minWidth as
  // ctaBtn so column widths stay aligned across rows.
  ctaBtnRequested: {
    backgroundColor:
      (fpColors as any).primaryContainer ?? fpColors.surfaceVariant ?? '#EADDFF',
  },
  ctaBtnRequestedText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color:
      (fpColors as any).onPrimaryContainer ?? fpColors.primary,
    letterSpacing: 0.2,
  },
  // FE-248 v2 — "Lent out" CTA for owners whose item is currently held
  // by another member. Uses the tertiary container (warmer than the
  // primary purple) so the eye registers "out" without it shouting
  // error red.
  ctaBtnLentOut: {
    backgroundColor:
      (fpColors as any).tertiaryContainer ??
      (fpColors as any).surfaceVariant ??
      '#FFE0B2',
  },
  ctaBtnLentOutText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color:
      (fpColors as any).onTertiaryContainer ?? fpColors.primary,
    letterSpacing: 0.2,
  },
  loadMore: {
    marginTop: 8,
    marginHorizontal: 16,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  loadMoreText: {
    fontSize: 13,
    color: fpColors.primary,
    fontWeight: '600',
  },
  allLoaded: {
    textAlign: 'center',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    paddingVertical: 12,
  },
});

export default GroupInventorySection;
